import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

/**
 * Deep equality comparison for browser environments
 * Optimized with Set for O(1) key lookups
 * Replacement for Node.js util.isDeepStrictEqual
 */
function isDeepEqual(obj1, obj2) {
  // Same reference or both null/undefined
  if (obj1 === obj2) return true;

  // Type check
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  // Handle Date objects
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Handle RegExp objects
  if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
    return obj1.toString() === obj2.toString();
  }

  // Array check
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  // Get keys
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Different number of keys
  if (keys1.length !== keys2.length) return false;

  // Use Set for O(1) lookup instead of includes() O(n)
  const keys2Set = new Set(keys2);

  // Recursively compare values
  for (const key of keys1) {
    if (!keys2Set.has(key)) return false;
    if (!isDeepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * Enhanced useDirtyChecker Hook
 *
 * Tracks changes between initial data and current data to prevent unnecessary API calls.
 * Uses deep comparison to detect actual changes in nested objects/arrays.
 * Supports custom validation with detailed error reporting.
 * NEW: Supports returning updated items when key comparison is provided.
 *
 * @param {Object} initialData - The original/initial data object
 * @param {Object} currentData - The current/modified data object
 * @param {Object} options - Configuration options
 * @param {Function} options.validate - Custom validation function (data) => { isValid, errors: {} }
 * @param {number} options.validationDelay - Debounce delay in ms (default: 500ms)
 * @param {Array|Object} options.compareKeys - Keys to compare for updated items detection
 *   - If Array: Return array of updated items from currentData
 *   - If Object: Return object with updated items grouped by key
 *   - If not provided: Existing behavior (return changed field names)
 *
 * @returns {Object} - {
 *   isDirty,
 *   changedFields,
 *   updatedItems, // NEW: Updated items based on compareKeys
 *   isValid,
 *   validationErrors: {},
 *   reset,
 *   getInitialData,
 *   validateNow
 * }
 *
 * @example
 * // Basic usage (existing functionality)
 * const { isDirty, changedFields, reset } = useDirtyChecker(
 *   initialData,
 *   currentData
 * );
 *
 * @example
 * // NEW: Return updated items array (for list data)
 * const { isDirty, updatedItems } = useDirtyChecker(
 *   initialMilestones,
 *   currentMilestones,
 *   { compareKeys: ['id', 'title', 'amount'] } // Return array of changed milestones
 * );
 *
 * @example
 * // NEW: Return updated items object (for grouped data)
 * const { isDirty, updatedItems } = useDirtyChecker(
 *   initialData,
 *   currentData,
 *   {
 *     compareKeys: {
 *       profile: ['name', 'email'],    // Compare these keys in profile object
 *       settings: ['theme', 'language'] // Compare these keys in settings object
 *     }
 *   }
 * );
 * // Returns: { profile: { name: 'John' }, settings: { theme: 'dark' } }
 */
export function useDirtyChecker(initialData, currentData, options = {}) {
  const {
    validate = null,
    validationDelay = 500,
    compareKeys = null,
  } = options;
  const initialRef = useRef(initialData);
  const prevCurrentRef = useRef(currentData);

  const [isDirty, setIsDirty] = useState(false);
  const [changedFields, setChangedFields] = useState([]);
  const [updatedItems, setUpdatedItems] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const validationTimeoutRef = useRef(null);

  /**
   * Check if an item has changes based on keys to compare
   * Optimized with early return on first change detected
   * @param {Array} keysToCompare - Array of keys to compare
   * @param {Object} initialItem - Initial item
   * @param {Object} currentItem - Current item
   * @returns {boolean} Whether the item has changes
   */
  const hasItemChanges = useCallback(
    (keysToCompare, initialItem, currentItem) => {
      // Input validation
      if (!Array.isArray(keysToCompare) || keysToCompare.length === 0) {
        return false;
      }
      if (!initialItem || !currentItem) {
        return true;
      }

      // Use for loop for early return optimization
      for (let i = 0; i < keysToCompare.length; i++) {
        const key = keysToCompare[i];
        const initialValue = initialItem[key];
        const currentValue = currentItem[key];

        if (!isDeepEqual(initialValue, currentValue)) {
          return true; // Early return on first change
        }
      }

      return false;
    },
    []
  );

  /**
   * Get updated items based on compareKeys configuration
   * Optimized with early returns and efficient Map lookups
   * @param {Array|Object} keysConfig - Configuration for comparison
   * @param {Array} initialData - Initial data array
   * @param {Array} currentData - Current data array
   * @returns {Array|Object|null} Updated items or null if no compareKeys
   */
  const getUpdatedItems = useCallback(
    (keysConfig, initialData, currentData) => {
      // Input validation
      if (!keysConfig || !Array.isArray(keysConfig)) {
        return null;
      }
      if (!Array.isArray(initialData) || !Array.isArray(currentData)) {
        return null;
      }

      // Early return if no current data
      if (currentData.length === 0) {
        return [];
      }

      // Create map of initial items for O(1) lookup
      const initialMap = new Map();
      for (let i = 0; i < initialData.length; i++) {
        const item = initialData[i];
        if (item && item.id != null) {
          initialMap.set(item.id, item);
        }
      }

      const updated = [];

      for (let i = 0; i < currentData.length; i++) {
        const currentItem = currentData[i];
        if (!currentItem) continue;

        const initialItem = currentItem.id
          ? initialMap.get(currentItem.id)
          : null;

        // If it's a new item or has changes
        if (
          !initialItem ||
          !currentItem.id ||
          hasItemChanges(keysConfig, initialItem, currentItem)
        ) {
          updated.push({ ...currentItem });
        }
      }

      return updated.length > 0 ? updated : [];
    },
    [hasItemChanges]
  );

  /**
   * Get grouped updated items for object-based compareKeys
   * Optimized with early validation and efficient iteration
   * @param {Object} keysConfig - Object with grouped keys
   * @param {Object} initialData - Initial data object
   * @param {Object} currentData - Current data object
   * @returns {Object} Grouped updated items
   */
  const getGroupedUpdatedItems = useCallback(
    (keysConfig, initialData, currentData) => {
      // Input validation
      if (
        !keysConfig ||
        typeof keysConfig !== 'object' ||
        Array.isArray(keysConfig)
      ) {
        return {};
      }

      if (!initialData || !currentData) {
        return {};
      }

      const updated = {};
      const groupKeys = Object.keys(keysConfig);

      for (let i = 0; i < groupKeys.length; i++) {
        const groupKey = groupKeys[i];
        const keysToCompare = keysConfig[groupKey];

        if (!Array.isArray(keysToCompare) || keysToCompare.length === 0) {
          continue;
        }

        const initialGroup = initialData[groupKey] || {};
        const currentGroup = currentData[groupKey] || {};

        const groupUpdated = {};

        for (let j = 0; j < keysToCompare.length; j++) {
          const key = keysToCompare[j];
          if (!isDeepEqual(initialGroup[key], currentGroup[key])) {
            groupUpdated[key] = currentGroup[key];
          }
        }

        if (Object.keys(groupUpdated).length > 0) {
          updated[groupKey] = groupUpdated;
        }
      }

      return updated;
    },
    []
  );

  // Only update if data actually changed (shallow comparison first)
  const memoCurrent = useMemo(() => {
    if (prevCurrentRef.current === currentData) {
      return prevCurrentRef.current;
    }
    prevCurrentRef.current = currentData;
    return currentData;
  }, [currentData]);

  // Run validation with debounce to prevent validation on every keystroke
  useEffect(() => {
    if (!validate || !memoCurrent) {
      setIsValid(true);
      setValidationErrors({});
      return;
    }

    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Debounce validation
    validationTimeoutRef.current = setTimeout(() => {
      try {
        const result = validate(memoCurrent);

        // Support both { isValid, errors } formats (array or object)
        if (typeof result === 'object' && result !== null) {
          const newIsValid = result.isValid ?? true;
          const newErrors = result.errors || {};

          // Only update state if values actually changed (prevent re-renders)
          setIsValid((prev) => (prev !== newIsValid ? newIsValid : prev));
          setValidationErrors((prev) => {
            // Deep compare errors to prevent unnecessary updates
            if (isDeepEqual(prev, newErrors)) return prev;
            return newErrors;
          });
        } else {
          // If validation returns boolean, treat as isValid
          setIsValid(!!result);
          setValidationErrors({});
        }
      } catch (error) {
        console.error('Validation function error:', error);
        setIsValid(false);
        setValidationErrors({ _error: `Validation error: ${error.message}` });
      }
    }, validationDelay);

    // Cleanup timeout on unmount or data change
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [memoCurrent, validate, validationDelay]);

  useEffect(() => {
    // Skip comparison if either data is null/undefined
    if (!initialRef.current || !memoCurrent) {
      setIsDirty(false);
      setChangedFields([]);
      setUpdatedItems(null);
      return;
    }

    const diffs = [];

    // Compare all keys from both initial and current data
    const allKeys = new Set([
      ...Object.keys(initialRef.current),
      ...Object.keys(memoCurrent),
    ]);

    // Optimized iteration with for...of
    for (const key of allKeys) {
      if (!isDeepEqual(initialRef.current[key], memoCurrent[key])) {
        diffs.push(key);
      }
    }

    // Calculate updated items if compareKeys is provided
    let newUpdatedItems = null;
    if (compareKeys) {
      try {
        if (Array.isArray(compareKeys)) {
          // Array compareKeys: return array of updated items
          newUpdatedItems = getUpdatedItems(
            compareKeys,
            initialRef.current,
            memoCurrent
          );
        } else if (typeof compareKeys === 'object') {
          // Object compareKeys: return grouped updated items
          newUpdatedItems = getGroupedUpdatedItems(
            compareKeys,
            initialRef.current,
            memoCurrent
          );
        }
      } catch (error) {
        console.error('Error calculating updated items:', error);
        newUpdatedItems = null;
      }
    }

    // Calculate isDirty state efficiently
    const hasFieldChanges = diffs.length > 0;
    const hasItemChanges = newUpdatedItems
      ? Array.isArray(newUpdatedItems)
        ? newUpdatedItems.length > 0
        : Object.keys(newUpdatedItems).length > 0
      : false;
    const newIsDirty = hasFieldChanges || hasItemChanges;

    // Only update state if values actually changed (prevent re-renders)
    setChangedFields((prev) => {
      if (isDeepEqual(prev, diffs)) return prev;
      return diffs;
    });
    setUpdatedItems((prev) => {
      if (isDeepEqual(prev, newUpdatedItems)) return prev;
      return newUpdatedItems;
    });
    setIsDirty((prev) => (prev !== newIsDirty ? newIsDirty : prev));
  }, [memoCurrent, compareKeys, getUpdatedItems, getGroupedUpdatedItems]);

  /**
   * Reset the dirty state with new baseline data
   * @param {Object} newInitial - New baseline data (defaults to current data)
   */
  const reset = useCallback(
    (newInitial) => {
      // Clear pending validation timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }

      const baseline = newInitial || memoCurrent;
      initialRef.current = baseline;
      prevCurrentRef.current = baseline;
      setIsDirty(false);
      setChangedFields([]);
      setUpdatedItems(null);
      setIsValid(true);
      setValidationErrors({});
    },
    [memoCurrent]
  );

  /**
   * Get the current initial/baseline data
   * @returns {Object} The initial data being compared against
   */
  const getInitialData = useCallback(() => initialRef.current, []);

  /**
   * Run validation immediately (bypass debounce)
   * Useful for validating before save
   * @returns {Object} { isValid, errors }
   */
  const validateNow = useCallback(() => {
    if (!validate || !memoCurrent) {
      return { isValid: true, errors: {} };
    }

    try {
      const result = validate(memoCurrent);

      if (typeof result === 'object' && result !== null) {
        const newIsValid = result.isValid ?? true;
        const newErrors = result.errors || {};

        // Update state immediately
        setIsValid(newIsValid);
        setValidationErrors(newErrors);

        return { isValid: newIsValid, errors: newErrors };
      } else {
        const isValidResult = !!result;
        setIsValid(isValidResult);
        setValidationErrors({});
        return { isValid: isValidResult, errors: {} };
      }
    } catch (error) {
      console.error('Validation function error:', error);
      const errorObj = { _error: `Validation error: ${error.message}` };
      setIsValid(false);
      setValidationErrors(errorObj);
      return { isValid: false, errors: errorObj };
    }
  }, [validate, memoCurrent]);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isDirty,
      changedFields,
      updatedItems, // NEW: Updated items based on compareKeys
      isValid,
      validationErrors,
      reset,
      getInitialData,
      validateNow,
    }),
    [
      isDirty,
      changedFields,
      updatedItems,
      isValid,
      validationErrors,
      reset,
      getInitialData,
      validateNow,
    ]
  );
}
